import {useEffect, useMemo, useRef, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {BarChart3, ExternalLink, KeyRound, Pencil, Trash2, UserPlus, X} from "lucide-react";
import {useTranslation} from "../../../shared/i18n";
import {DataState} from "../../../shared/components/DataState";
import {ConfirmationDialog} from "../../../shared/components/ConfirmationDialog";
import {mediaUrl} from "../../../shared/components/BusinessBlocks";
import {MutationResultBanner} from "../../../shared/components/MutationResultBanner";
import {StatusBadge} from "../../../shared/components/StatusBadge";
import {formatDateTime, formatMoney, formatNumber} from "../../../shared/utils/format";
import {
    createStaffMember,
    deleteStaffMember,
    loadBranchStaff,
    loadBranchStaffStatistics,
    loadStaffMember,
    loadStaffMemberStatistics,
    loadStaffProperties,
    rotateStaffCredential,
    updateStaffMember,
    uploadStaffAvatar,
} from "../api";
import {invalidateBranchLifecycleQueries} from "../queryInvalidation";
import {mapStaffApiError} from "../staffErrors";
import {CredentialRotationDialog} from "./CredentialRotationDialog";
import {StaffEditorDialog} from "./StaffEditorDialog";
import "../staff.css";

const PERIODS = [7, 30, 90];

const metricRows = (statistics, includeStaff, t) => [
    includeStaff ? {label: t("orgs.staff.metrics.staff"), value: formatNumber(statistics?.total_staff)} : null,
    {label: t("orgs.staff.metrics.invited"), value: formatNumber(statistics?.total_invited)},
    {label: t("orgs.staff.metrics.transactions"), value: formatNumber(statistics?.total_transactions)},
    {label: t("orgs.staff.metrics.refBonuses"), value: formatMoney(statistics?.ref_bonuses_earned)},
    {label: t("orgs.staff.metrics.txBonuses"), value: formatMoney(statistics?.transactions_bonuses_earned)},
    {label: t("orgs.staff.metrics.points"), value: formatMoney(statistics?.total_earned_points)},
].filter(Boolean);

const StatisticsPanel = ({title, statistics, loading, error, includeStaff = false}) => {
    const {t} = useTranslation();
    return (
    <article className="staff-statistics-panel">
        <h3>{title}</h3>
        {loading ? (
            <p className="staff-state-line" role="status">{t("orgs.staff.loadingStats")}</p>
        ) : error ? (
            <p className="staff-state-line staff-state-error" role="alert">{mapStaffApiError(error).message}</p>
        ) : (
            <dl className="staff-metrics">
                {metricRows(statistics, includeStaff, t).map((metric) => (
                    <div key={metric.label}>
                        <dt>{metric.label}</dt>
                        <dd>{metric.value}</dd>
                    </div>
                ))}
            </dl>
        )}
    </article>
    );
};

const StaffAvatar = ({person}) => {
    const {t} = useTranslation();
    return person.avatar ? (
    <img className="staff-member-avatar" alt={t("orgs.staff.avatarAlt", {name: person.name})} src={mediaUrl(person.avatar)} />
) : (
    <span className="staff-member-avatar staff-member-avatar-placeholder" aria-hidden="true">
        {String(person.name || "?").trim().slice(0, 1).toUpperCase()}
    </span>
    );
};

export const StaffWorkspace = ({branchId, placeId, branchName, staffDetails = [], canManage}) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const branchKey = String(branchId);
    const mountedRef = useRef(false);
    const activeBranchKeyRef = useRef(branchKey);
    activeBranchKeyRef.current = branchKey;
    const [days, setDays] = useState(30);
    const [statisticsPersonId, setStatisticsPersonId] = useState(null);
    const [editorMode, setEditorMode] = useState(null);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [actionDialog, setActionDialog] = useState(null);
    const [editorError, setEditorError] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [mutationResult, setMutationResult] = useState(null);
    const [credentialResult, setCredentialResult] = useState(null);
    const [createPending, setCreatePending] = useState(false);
    const [credentialPending, setCredentialPending] = useState(false);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const isActiveRequest = (request) => (
        mountedRef.current && activeBranchKeyRef.current === request.branchKey
    );

    const staffQuery = useQuery({
        queryKey: ["admin-branch-staff", branchKey],
        queryFn: () => loadBranchStaff(branchId),
        enabled: Boolean(branchId),
    });
    const propertiesQuery = useQuery({
        queryKey: ["admin-staff-properties"],
        queryFn: loadStaffProperties,
        enabled: Boolean(canManage),
    });
    const branchStatisticsQuery = useQuery({
        queryKey: ["admin-branch-staff-statistics", branchKey, days],
        queryFn: () => loadBranchStaffStatistics(branchId, days),
        enabled: Boolean(branchId),
    });
    const staffRows = Array.isArray(staffQuery.data?.staff) ? staffQuery.data.staff : [];
    const resolvedBranchName = staffQuery.data?.branch_name || branchName || t("orgs.staff.branchNumber", {id: branchId});
    const statisticsPerson = staffRows.find((person) => person.id === statisticsPersonId) || null;
    const personStatisticsQuery = useQuery({
        queryKey: ["admin-staff-member-statistics", String(statisticsPersonId || ""), days],
        queryFn: () => loadStaffMemberStatistics(statisticsPersonId, days),
        enabled: Boolean(statisticsPersonId),
    });
    const detailQuery = useQuery({
        queryKey: ["admin-staff-member", String(selectedStaff?.id || "")],
        queryFn: () => loadStaffMember(selectedStaff.id),
        enabled: editorMode === "edit" && Boolean(selectedStaff?.id),
    });
    const detailFallback = useMemo(
        () => staffDetails.find((person) => person.id === selectedStaff?.id) || {},
        [selectedStaff?.id, staffDetails],
    );
    const editorValue = useMemo(
        () => ({...detailFallback, ...selectedStaff, ...detailQuery.data}),
        [detailFallback, detailQuery.data, selectedStaff],
    );

    const invalidateStaffQueries = (request) => {
        void queryClient.invalidateQueries({queryKey: ["admin-branch-staff", request.branchKey]});
        void queryClient.invalidateQueries({queryKey: ["admin-branch-staff-statistics", request.branchKey]});
        void queryClient.invalidateQueries({queryKey: ["admin-staff-member"]});
        void queryClient.invalidateQueries({queryKey: ["admin-staff-member-statistics"]});
        invalidateBranchLifecycleQueries(queryClient, {
            placeId: request.placeId,
            branchId: request.branchId,
        });
    };

    const currentRequest = (personalId = null) => ({
        branchId,
        branchKey,
        personalId,
        placeId,
    });

    const closeEditor = () => {
        if (createPending || updateMutation.isPending || avatarMutation.isPending) return;
        setEditorMode(null);
        setSelectedStaff(null);
        setEditorError(null);
    };

    const updateMutation = useMutation({
        mutationFn: ({personalId, payload}) => updateStaffMember(personalId, payload),
        onSuccess: (_response, request) => {
            if (!isActiveRequest(request)) return;
            invalidateStaffQueries(request);
            setEditorMode(null);
            setSelectedStaff(null);
            setEditorError(null);
            setMutationResult({kind: "success", message: t("orgs.staff.profileUpdated")});
        },
        onError: (error, request) => {
            if (isActiveRequest(request)) setEditorError(mapStaffApiError(error));
        },
    });
    const avatarMutation = useMutation({
        mutationFn: ({personalId, avatar}) => uploadStaffAvatar(personalId, avatar),
        onSuccess: (_response, request) => {
            if (!isActiveRequest(request)) return;
            invalidateStaffQueries(request);
            setEditorMode(null);
            setSelectedStaff(null);
            setEditorError(null);
            setMutationResult({kind: "success", message: t("orgs.staff.avatarUpdated")});
        },
        onError: (error, request) => {
            if (isActiveRequest(request)) setEditorError(mapStaffApiError(error));
        },
    });
    const rotateCredential = async (password) => {
        const request = currentRequest(selectedStaff?.id);
        if (!request.personalId) return;
        setCredentialPending(true);
        setActionError(null);
        try {
            const response = await rotateStaffCredential(request.personalId, password);
            if (!isActiveRequest(request)) return;
            invalidateStaffQueries(request);
            setActionDialog(null);
            setSelectedStaff(null);
            setMutationResult({kind: "success", message: response?.message || t("orgs.common.credentialsUpdated")});
        } catch (error) {
            if (isActiveRequest(request)) setActionError(mapStaffApiError(error));
        } finally {
            if (isActiveRequest(request)) setCredentialPending(false);
        }
    };
    const deleteMutation = useMutation({
        mutationFn: ({personalId, branchId: requestBranchId}) => deleteStaffMember(personalId, requestBranchId),
        onSuccess: (_response, request) => {
            if (!isActiveRequest(request)) return;
            invalidateStaffQueries(request);
            setStatisticsPersonId((current) => current === request.personalId ? null : current);
            setActionDialog(null);
            setSelectedStaff(null);
            setActionError(null);
            setMutationResult({kind: "success", message: t("orgs.staff.deleted")});
        },
        onError: (error, request) => {
            if (isActiveRequest(request)) setActionError(mapStaffApiError(error));
        },
    });

    const createStaff = async (payload) => {
        const request = {
            ...currentRequest(),
            payload: {...payload, branch: resolvedBranchName},
        };
        setCreatePending(true);
        setEditorError(null);
        try {
            const response = await createStaffMember(request.payload);
            if (!isActiveRequest(request)) return;
            setCredentialResult({
                message: response?.message || t("orgs.staff.created"),
                credential: response?.credential || "",
            });
            setEditorMode(null);
            setMutationResult(null);
            invalidateStaffQueries(request);
        } catch (error) {
            if (isActiveRequest(request)) setEditorError(mapStaffApiError(error));
        } finally {
            if (isActiveRequest(request)) setCreatePending(false);
        }
    };

    const openEditor = (mode, person = null) => {
        setMutationResult(null);
        setEditorError(null);
        setSelectedStaff(person);
        setEditorMode(mode);
    };

    const openAction = (dialog, person) => {
        setMutationResult(null);
        setActionError(null);
        setSelectedStaff(person);
        setActionDialog(dialog);
    };

    const closeAction = () => {
        if (credentialPending || deleteMutation.isPending) return;
        setActionDialog(null);
        setSelectedStaff(null);
        setActionError(null);
    };

    const editorQueryError = detailQuery.isError
        ? mapStaffApiError(detailQuery.error)
        : propertiesQuery.isError
            ? mapStaffApiError(propertiesQuery.error)
            : null;

    return (
        <section className="staff-workspace" aria-labelledby="staff-workspace-heading">
            <header className="staff-workspace-header">
                <div>
                    <p className="admin-eyebrow">{t("orgs.staff.eyebrow")}</p>
                    <h2 id="staff-workspace-heading">{t("orgs.staff.title")}</h2>
                    <p>{t("orgs.staff.copy")}</p>
                </div>
                {canManage ? (
                    <button className="admin-button admin-button-primary" type="button" onClick={() => openEditor("create")}>
                        <UserPlus aria-hidden="true" size={16} /> {t("orgs.staff.add")}
                    </button>
                ) : null}
            </header>

            {credentialResult ? (
                <section className="staff-credential-result" role="status" aria-label={t("orgs.staff.credentialsLabel")}>
                    <div>
                        <strong>{t("orgs.staff.saveNow")}</strong>
                        <p>{t("orgs.staff.credentialsNote", {message: credentialResult.message})}</p>
                        <code>{credentialResult.credential}</code>
                    </div>
                    <button type="button" aria-label={t("orgs.staff.hideCredentials")} title={t("orgs.staff.hideCredentials")} onClick={() => setCredentialResult(null)}>
                        <X aria-hidden="true" size={18} />
                    </button>
                </section>
            ) : null}
            <MutationResultBanner result={mutationResult} />

            <div className="staff-statistics-toolbar">
                <div className="staff-period-control" role="group" aria-label={t("orgs.staff.period")}>
                    {PERIODS.map((period) => (
                        <button
                            key={period}
                            type="button"
                            aria-pressed={days === period}
                            className={days === period ? "is-active" : ""}
                            onClick={() => setDays(period)}
                        >
                            {t("orgs.staff.days", {count: period})}
                        </button>
                    ))}
                </div>
                <label className="staff-statistics-select">
                    <span>{t("orgs.staff.personStats")}</span>
                    <select
                        value={statisticsPersonId || ""}
                        disabled={staffQuery.isLoading || !staffRows.length}
                        onChange={(event) => setStatisticsPersonId(event.target.value ? Number(event.target.value) : null)}
                    >
                        <option value="">{t("orgs.staff.notSelected")}</option>
                        {staffRows.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
                    </select>
                </label>
            </div>

            <div className="staff-statistics-grid">
                <StatisticsPanel
                    title={t("orgs.staff.branchStats")}
                    statistics={branchStatisticsQuery.data}
                    loading={branchStatisticsQuery.isLoading}
                    error={branchStatisticsQuery.error}
                    includeStaff
                />
                {statisticsPerson ? (
                    <StatisticsPanel
                        title={t("orgs.staff.personStatsNamed", {name: statisticsPerson.name})}
                        statistics={personStatisticsQuery.data}
                        loading={personStatisticsQuery.isLoading}
                        error={personStatisticsQuery.error}
                    />
                ) : (
                    <article className="staff-statistics-panel staff-statistics-placeholder">
                        <BarChart3 aria-hidden="true" size={20} />
                        <h3>{t("orgs.staff.personStats")}</h3>
                        <p>{t("orgs.staff.pickPerson")}</p>
                    </article>
                )}
            </div>

            <div className="staff-list-heading">
                <div>
                    <h3>{t("orgs.staff.listTitle")}</h3>
                    <span>{t("orgs.staff.people", {count: formatNumber(staffQuery.data?.total_staff)})}</span>
                </div>
            </div>
            {staffQuery.isLoading ? (
                <DataState title={t("orgs.staff.loadingTitle")} copy={t("orgs.staff.loadingCopy")} />
            ) : staffQuery.isError ? (
                <div role="alert">
                    <DataState title={t("orgs.staff.errorTitle")} copy={mapStaffApiError(staffQuery.error).message} />
                </div>
            ) : !staffRows.length ? (
                <DataState title={t("orgs.staff.emptyTitle")} copy={t("orgs.staff.emptyCopy")} />
            ) : (
                <div className="staff-member-grid">
                    {staffRows.map((person) => (
                        <article className="staff-member-card" key={person.id}>
                            <div className="staff-member-main">
                                <StaffAvatar person={person} />
                                <div>
                                    <div className="staff-member-name">
                                        <h4>{person.name || t("orgs.staff.unnamed")}</h4>
                                        {person.is_top_1 ? <StatusBadge tone="success">{t("orgs.staff.leader")}</StatusBadge> : null}
                                    </div>
                                    <p>{person.position || t("orgs.staff.noPosition")}</p>
                                    <a href={`tel:${person.phone_number}`}>{person.phone_number || t("orgs.staff.noPhone")}</a>
                                </div>
                            </div>
                            <dl className="staff-member-facts">
                                <div><dt>{t("orgs.staff.transactions")}</dt><dd>{formatNumber(person.transactions_count)}</dd></div>
                                <div><dt>{t("orgs.staff.added")}</dt><dd>{formatDateTime(person.created_at)}</dd></div>
                            </dl>
                            <footer className="staff-member-actions">
                                <button
                                    type="button"
                                    aria-label={t("orgs.staff.showStats", {name: person.name})}
                                    title={t("orgs.staff.showStatsTitle")}
                                    onClick={() => setStatisticsPersonId(person.id)}
                                >
                                    <BarChart3 aria-hidden="true" size={16} />
                                </button>
                                {person.ref_link ? (
                                    <a href={person.ref_link} target="_blank" rel="noreferrer" aria-label={t("orgs.staff.openRef", {name: person.name})} title={t("orgs.staff.openRefTitle")}>
                                        <ExternalLink aria-hidden="true" size={16} />
                                    </a>
                                ) : null}
                                {canManage ? (
                                    <>
                                        <button type="button" aria-label={t("orgs.common.editNamed", {name: person.name})} title={t("orgs.common.edit")} onClick={() => openEditor("edit", person)}>
                                            <Pencil aria-hidden="true" size={16} />
                                        </button>
                                        <button type="button" aria-label={t("orgs.staff.changePassword", {name: person.name})} title={t("orgs.credential.title")} onClick={() => openAction("credential", person)}>
                                            <KeyRound aria-hidden="true" size={16} />
                                        </button>
                                        <button className="is-danger" type="button" aria-label={t("orgs.staff.delete", {name: person.name})} title={t("orgs.staff.deleteTitle")} onClick={() => openAction("delete", person)}>
                                            <Trash2 aria-hidden="true" size={16} />
                                        </button>
                                    </>
                                ) : null}
                            </footer>
                        </article>
                    ))}
                </div>
            )}

            <StaffEditorDialog
                open={Boolean(editorMode)}
                mode={editorMode}
                initialValue={editorMode === "edit" ? editorValue : null}
                branchName={resolvedBranchName}
                properties={propertiesQuery.data}
                loading={editorMode === "edit" && detailQuery.isLoading}
                pending={createPending || updateMutation.isPending}
                avatarPending={avatarMutation.isPending}
                error={editorError || editorQueryError}
                onCancel={closeEditor}
                onSubmit={(payload) => editorMode === "create"
                    ? createStaff(payload)
                    : updateMutation.mutate({...currentRequest(selectedStaff?.id), payload})}
                onAvatarUpload={(avatar) => avatarMutation.mutate({...currentRequest(selectedStaff?.id), avatar})}
            />
            <CredentialRotationDialog
                open={actionDialog === "credential"}
                entity={selectedStaff?.name}
                pending={credentialPending}
                error={actionError}
                onCancel={closeAction}
                onConfirm={rotateCredential}
            />
            <ConfirmationDialog
                open={actionDialog === "delete"}
                title={t("orgs.staff.deleteDialogTitle")}
                description={t("orgs.staff.deleteDialogCopy")}
                entity={selectedStaff?.name}
                confirmationText={selectedStaff?.name}
                confirmLabel={t("orgs.staff.deleteConfirm")}
                tone="destructive"
                pending={deleteMutation.isPending}
                error={actionError}
                dialogKey={`delete-staff-${selectedStaff?.id || ""}`}
                onCancel={closeAction}
                onConfirm={() => deleteMutation.mutate(currentRequest(selectedStaff?.id))}
            />
        </section>
    );
};
